import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { ArrowLeft, Images, Loader2, Search, Calendar, Tag as TagIcon, ArrowUpDown, X } from "lucide-react";
import { Footer } from "@/components/landing/FooterCTA";
import { SponsorBanner } from "@/components/Sponsors";
import { Navbar } from "@/components/landing/Navbar";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import logo from "../assets/logo.png";

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const FULL_MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

function albumDate(a) {
  return a.event_date || (a.created_at ? a.created_at.slice(0, 10) : null);
}

function fmtDate(d) {
  if (!d) return null;
  const [y, m, day] = d.slice(0, 10).split("-");
  if (!y) return null;
  if (day && m) return `${parseInt(day, 10)} ${MONTHS[parseInt(m, 10) - 1]} ${y}`;
  return y;
}

function AlbumCard({ album, i, onTag }) {
  const d = albumDate(album);
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.3) }}
      data-testid={`album-card-${album.slug}`}
    >
      <Link
        to={`/galerias/${album.slug}`}
        data-testid={`album-open-${album.slug}`}
        className="group block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
      >
        <div className="relative aspect-[4/3] bg-navy overflow-hidden">
          {album.cover_url ? (
            <img
              src={album.cover_url}
              alt={album.name}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gold">
              <Images className="h-10 w-10" />
            </div>
          )}
          <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider text-navy bg-gold px-2.5 py-1 rounded-full">
            {album.category}
          </span>
          <span className="absolute bottom-3 right-3 text-xs font-semibold text-white bg-black/45 backdrop-blur-sm px-2.5 py-1 rounded-full">
            {album.photo_count} foto{album.photo_count === 1 ? "" : "s"}
          </span>
        </div>
        <div className="p-4 sm:p-5">
          {d && (
            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400">
              <Calendar className="h-3.5 w-3.5" /> {fmtDate(d)}
            </div>
          )}
          <h3 className="mt-1.5 text-lg font-bold text-navy leading-tight line-clamp-2">{album.name}</h3>
          {album.description && (
            <p className="mt-1.5 text-sm text-gray-500 line-clamp-2">{album.description}</p>
          )}
          {album.tags?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {album.tags.slice(0, 4).map((t) => (
                <button
                  key={t}
                  onClick={(e) => { e.preventDefault(); onTag(t); }}
                  data-testid={`album-tag-${t}`}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-navy bg-[#eef1f7] hover:bg-gold/30 px-2 py-1 rounded-full transition-colors"
                >
                  <TagIcon className="h-3 w-3" /> {t}
                </button>
              ))}
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
}

export default function GaleriasPage() {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("Todas");
  const [year, setYear] = useState("Todos");
  const [sort, setSort] = useState("recent");

  useEffect(() => {
    api.get("/gallery/albums")
      .then(({ data }) => setAlbums(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () => ["Todas", ...Array.from(new Set(albums.map((a) => a.category)))],
    [albums]
  );

  const years = useMemo(() => {
    const ys = new Set();
    albums.forEach((a) => { if (a.event_date) ys.add(a.event_date.slice(0, 4)); });
    return ["Todos", ...Array.from(ys).sort((x, y2) => y2.localeCompare(x))];
  }, [albums]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = albums.filter((a) => {
      if (category !== "Todas" && a.category !== category) return false;
      if (year !== "Todos" && (!a.event_date || a.event_date.slice(0, 4) !== year)) return false;
      if (term) {
        let dateStr = "";
        if (a.event_date) {
          const [yy, mm] = a.event_date.slice(0, 10).split("-");
          const mi = parseInt(mm, 10) - 1;
          dateStr = `${a.event_date} ${fmtDate(a.event_date) || ""} ${FULL_MONTHS[mi] || ""} ${yy}`;
        }
        const hay = [a.name, a.description, dateStr, ...(a.tags || [])].join(" ").toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
    list = [...list].sort((a, b) => {
      const da = albumDate(a) || "", db = albumDate(b) || "";
      return sort === "recent" ? db.localeCompare(da) : da.localeCompare(db);
    });
    return list;
  }, [albums, q, category, year, sort]);

  const activeFilters = q || category !== "Todas" || year !== "Todos";

  return (
    <div className="min-h-screen bg-[#eef1f7] flex flex-col">
      <Navbar />
      <div className="pt-16 sm:pt-20">
        <Breadcrumbs items={[{ label: "Galería" }]} />
      </div>

      {/* Title */}
      <section className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-10 pt-4">
          <span className="section-tag section-tag--dark">Galería de fotos</span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight text-white">Nuestros álbumes</h1>
          <p className="mt-3 text-base sm:text-lg text-white/75 max-w-2xl">
            Revive los mejores momentos del club. Buscá, filtrá por categoría o fecha y abrí un álbum para ver todas sus fotos.
          </p>
        </div>
      </section>

      {/* Search + filters */}
      <div className="bg-white/70 backdrop-blur border-b border-gray-100 sticky top-16 sm:top-20 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                data-testid="galerias-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por nombre, descripción o etiqueta…"
                className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-navy/30 text-sm"
              />
              {q && (
                <button onClick={() => setQ("")} aria-label="Limpiar" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-navy">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <select
                data-testid="galerias-year"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="flex-1 sm:flex-none px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-navy"
              >
                {years.map((y) => <option key={y} value={y}>{y === "Todos" ? "Todos los años" : y}</option>)}
              </select>
              <button
                data-testid="galerias-sort"
                onClick={() => setSort((s) => (s === "recent" ? "oldest" : "recent"))}
                className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-navy hover:border-navy/40"
              >
                <ArrowUpDown className="h-4 w-4" /> {sort === "recent" ? "Recientes" : "Antiguos"}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`galerias-filter-${c}`}
                onClick={() => setCategory(c)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                  category === c ? "bg-navy text-white" : "bg-white text-navy border border-gray-200 hover:border-navy/40"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-8">
        {loading ? (
          <div className="py-20 flex justify-center text-navy/40"><Loader2 className="h-7 w-7 animate-spin" /></div>
        ) : albums.length === 0 ? (
          <div className="py-20 text-center text-gray-400">
            <Images className="h-10 w-10 mx-auto" />
            <p className="mt-3">Todavía no hay álbumes publicados.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-gray-400">
            <Search className="h-9 w-9 mx-auto" />
            <p className="mt-3">No encontramos álbumes con esos filtros.</p>
            {activeFilters && (
              <button
                data-testid="galerias-clear-filters"
                onClick={() => { setQ(""); setCategory("Todas"); setYear("Todos"); }}
                className="mt-4 text-sm font-semibold text-navy underline"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            <p className="mb-5 text-sm text-gray-500" data-testid="galerias-count">
              {filtered.length} álbum{filtered.length === 1 ? "" : "es"}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((a, i) => (
                <AlbumCard key={a.id} album={a} i={i} onTag={(t) => setQ(t)} />
              ))}
            </div>
          </>
        )}
      </main>

      <SponsorBanner placement="banner_galerias" />
      <Footer />
    </div>
  );
}
